/**
 * Face-to-Face Encounter (Medicare HH) — options + empty payload.
 * Clinical sections live in dedicated-table `payload` JSON.
 */

import { buildDiagnosisAllergies } from "../../../patientForm.shared";

export const US_STATE_OPTIONS = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

export const ENCOUNTER_TIMING_OPTIONS = ["Before SOC", "After SOC"];

export const CMS_TIMEFRAME_OPTIONS = [
  "Yes, within timeframe",
  "No, outside timeframe",
  "Needs Review",
];

export const PLACE_OF_ENCOUNTER_OPTIONS = [
  "Physician Office",
  "Patient Home",
  "Hospital",
  "Inpatient Rehab Facility",
  "Skilled Nursing Facility",
  "Outpatient Facility",
  "Other",
];

export const ENCOUNTER_METHOD_OPTIONS = [
  "In-Person",
  "Telehealth",
  "Other CMS-permitted method",
];

export const TELEHEALTH_VERIFIED_OPTIONS = ["Yes", "No", "N/A"];

export const PRACTITIONER_ROLE_OPTIONS = [
  "Certifying Practitioner",
  "Practitioner Performing F2F",
  "Both",
];

export const CREDENTIAL_OPTIONS = ["MD", "DO", "NP", "CNS", "PA", "Other"];

export const ELIGIBILITY_CHECK_OPTIONS = [
  "Practitioner type verified",
  "State license/auth verified",
  "Practitioner permitted to perform encounter",
  "No financial relationship conflict identified",
];

export const ENCOUNTER_RELATED_OPTIONS = [
  "Yes",
  "No",
  "Needs clarification",
];

export const SYMPTOM_OPTIONS = [
  "Pain",
  "Weakness",
  "SOB / Dyspnea",
  "Fatigue",
  "Dizziness",
  "Difficulty Walking",
  "Functional Decline",
  "Wound",
  "Infection",
  "Medication Management",
  "Post-Hospitalization",
  "Other",
];

export const HOMEBOUND_CRITERION1_OPTIONS = [
  "Requires supportive device (cane/walker/wheelchair)",
  "Requires assistance of another person",
  "Requires special transportation",
  "Leaving home medically contraindicated",
  "Other",
];

export const YES_NO_OPTIONS = ["Yes", "No"];

export const HOMEBOUND_DETERMINATION_OPTIONS = [
  "Meets homebound requirements",
  "Does not meet homebound requirements",
  "Further documentation required",
];

export const SKILLED_DISCIPLINE_OPTIONS = [
  "Skilled Nursing",
  "Physical Therapy (PT)",
  "Speech-Language Pathology (SLP)",
  "Occupational Therapy (OT)",
  "Multiple disciplines",
];

export const SKILLED_NEED_OPTIONS = [
  "Wound care / dressing changes",
  "Catheter care",
  "IV / infusion therapy",
  "Injection administration",
  "Observation & assessment",
  "Teaching / training",
  "Medication management",
  "Fall-risk management",
  "Gait / transfer training",
  "Therapeutic exercise",
  "ADL training",
  "Swallowing / communication therapy",
  "Pain management",
  "Disease process teaching",
  "Post-surgical care",
  "Other skilled need",
];

export const REQUESTED_SERVICE_KEYS = [
  { key: "skilledNursing", label: "Skilled Nursing" },
  { key: "physicalTherapy", label: "Physical Therapy (PT)" },
  { key: "occupationalTherapy", label: "Occupational Therapy (OT)" },
  { key: "speechTherapy", label: "Speech Therapy (SLP)" },
  { key: "medicalSocialServices", label: "Medical Social Services" },
  { key: "homeHealthAide", label: "Home Health Aide" },
];

export const QA_STATUS_OPTIONS = [
  "Complete",
  "Incomplete",
  "Correction Requested",
  "Escalated",
  "Ready for Billing",
];

export const QA_CHECKLIST_OPTIONS = [
  "SOC date entered",
  "F2F encounter date entered",
  "Encounter within CMS timeframe",
  "Place of encounter documented",
  "Encounter method documented",
  "Practitioner identified with NPI",
  "Credentials documented",
  "Primary reason for HH documented",
  "Primary ICD-10 entered",
  "Homebound status documented",
  "Skilled need documented",
  "Requested services indicated",
  "Medical necessity narrative complete",
  "Practitioner attestation signed",
  "Certifying practitioner completed (if different)",
];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const today = () => new Date().toISOString().slice(0, 10);

export const emptyDiagnosisRow = (id) => ({
  id: id || `dx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  diagnosis: "",
  icd10: "",
  relevantToHh: "",
});

const hasChartDiagnosis = (row) =>
  Boolean(
    String(row?.primaryDiagnosis || "").trim() ||
      String(row?.diagnosisCode || "").trim(),
  );

const padOtherDiagnoses = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const count = Math.max(4, list.length);
  return Array.from({ length: count }, (_, i) => ({
    ...emptyDiagnosisRow(`dx-${i + 1}`),
    ...(list[i] || {}),
    id: list[i]?.id || `dx-${i + 1}`,
  }));
};

/**
 * Demographics "Diagnosis & Nutrition": first row = primary, rest = secondary.
 * Maps onto F2F §5 Primary Reason (condition + ICD-10) and Other Diagnosis rows.
 */
export const mapChartDiagnosesToF2fReason = (patient) => {
  const rows = buildDiagnosisAllergies(patient).filter(hasChartDiagnosis);
  const primary = rows[0] || {};
  const secondaries = rows.slice(1);
  return {
    primaryCondition: String(primary.primaryDiagnosis || "").trim(),
    primaryIcd10: String(primary.diagnosisCode || "").trim(),
    otherDiagnoses: padOtherDiagnoses(
      secondaries.map((row, i) => ({
        id: `dx-${i + 1}`,
        diagnosis: String(row.primaryDiagnosis || "").trim(),
        icd10: String(row.diagnosisCode || "").trim(),
        relevantToHh: "",
      })),
    ),
  };
};

export const otherDiagnosesAreEmpty = (rows) =>
  !(Array.isArray(rows) ? rows : []).some(
    (row) =>
      String(row?.diagnosis || "").trim() || String(row?.icd10 || "").trim(),
  );

export const emptyRequestedServices = () =>
  Object.fromEntries(
    REQUESTED_SERVICE_KEYS.map(({ key }) => [
      key,
      { selected: "", reason: "" },
    ]),
  );

export const emptyQaChecklist = () =>
  Object.fromEntries(QA_CHECKLIST_OPTIONS.map((item) => [item, false]));

/** Full editable Face-to-Face Encounter payload. */
export const emptyFaceToFacePayload = () => ({
  // —— 1. Patient Information ——
  patientName: "",
  dateOfBirth: "",
  mrn: "",
  mbi: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  primaryInsurance: "",
  secondaryInsurance: "",
  homeHealthAgency: "",
  agencyNpi: "",
  agencyCcn: "",

  // —— 2. Encounter / Episode ——
  socDate: "",
  certPeriodFrom: "",
  certPeriodTo: "",
  encounterDate: "",
  encounterTiming: "",
  daysFromEncounterToSoc: "",
  cmsTimeframeCheck: "",

  // —— 3. Encounter Type ——
  placeOfEncounter: [],
  placeOfEncounterOther: "",
  encounterMethod: "",
  encounterMethodOther: "",
  telehealthPlatform: "",
  telehealthPatientLocation: "",
  telehealthPractitionerLocation: "",
  telehealthRequirementsVerified: "",

  // —— 4. Practitioner ——
  practitionerName: "",
  practitionerRoles: [],
  credentials: [],
  credentialsOther: "",
  eligibilityChecks: [],
  practitionerNpi: "",
  stateLicense: "",
  specialty: "",
  organization: "",
  practitionerPhone: "",
  practitionerFax: "",
  verifiedBy: "",
  dateVerified: "",

  // —— 5. Primary Reason ——
  primaryCondition: "",
  primaryIcd10: "",
  otherDiagnoses: [
    emptyDiagnosisRow("dx-1"),
    emptyDiagnosisRow("dx-2"),
    emptyDiagnosisRow("dx-3"),
    emptyDiagnosisRow("dx-4"),
  ],
  encounterRelatedToHh: "",
  clinicalExplanationReason: "",

  // —— 6. Clinical Assessment ——
  chiefComplaint: "",
  symptoms: [],
  symptomOther: "",
  vitalsTemp: "",
  vitalsPulse: "",
  vitalsResp: "",
  vitalsBp: "",
  vitalsSpo2: "",
  vitalsWeight: "",
  vitalsPain: "",

  // —— 7. Homebound ——
  homeboundCriterion1: [],
  homeboundCriterion1Other: "",
  homeboundSupportingFindings: "",
  homeboundCriterion2: "",
  homeboundClinicalExplanation: "",
  homeboundDetermination: "",

  // —— 8. Skilled Need ——
  skilledDisciplines: [],
  skilledNeeds: [],
  skilledNeedFindings: "",

  // —— 9. Requested Services ——
  requestedServices: emptyRequestedServices(),

  // —— 10. Medical Necessity ——
  homeboundStatusSupport: "",
  skilledServicesSupport: "",
  expectedBenefit: "",
  recentHospitalization: "",
  hospitalizationDate: "",
  hospitalizationFacility: "",
  hospitalizationReason: "",
  diagnosticTests: "",

  // —— Clinician Authentication (above Office Review; stamp on dedicated cols) ——
  attestationEncounterDate: "",
  attestationPractitionerName: "",
  attestationCredentials: "",
  attestationNpi: "",

  // —— 12. Certifying Practitioner (if different) ——
  certifyingDifferent: false,
  certifyingName: "",
  certifyingCredentials: "",
  certifyingNpi: "",
  certifyingOrganization: "",
  certifyingPhone: "",
  certifyingAddress: "",
  certifyingSignedAt: "",

  // —— 13. QA Review ——
  qaChecklist: emptyQaChecklist(),
  qaStatus: "",
  qaReviewer: "",
  qaDate: "",
  qaComments: "",
});

export const F2F_ARRAY_KEYS = [
  "placeOfEncounter",
  "practitionerRoles",
  "credentials",
  "eligibilityChecks",
  "symptoms",
  "homeboundCriterion1",
  "skilledDisciplines",
  "skilledNeeds",
];
